import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';

interface ToastContextType {
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_DISPLAY_MS = 5000;
const TOAST_ANIMATION_MS = 400;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toastRef = useRef<HTMLDivElement | null>(null);
  const timeoutIdsRef = useRef<number[]>([]);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      timeoutIdsRef.current.forEach(id => clearTimeout(id));
      timeoutIdsRef.current = [];
      if (toastRef.current) {
        toastRef.current.remove();
        toastRef.current = null;
      }
    };
  }, []);

  // Safe timeout that checks if mounted
  const safeSetTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      if (isMountedRef.current) {
        callback();
      }
      timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== timeoutId);
    }, delay);
    timeoutIdsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  const showToast = useCallback((message: string) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    // Remove existing toast if present
    if (toastRef.current) {
      toastRef.current.remove();
      toastRef.current = null;
    }

    const toast = document.createElement('div');
    toast.textContent = message;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const style = toast.style;
    style.position = 'fixed';
    style.top = '16px';
    style.left = '50%';
    style.transform = 'translate(-50%, -28px) scale(0.92)';
    style.transformOrigin = 'center top';
    style.backgroundColor = '#e0f2ff';
    style.color = '#0f3c5c';
    style.padding = '12px 28px';
    style.borderRadius = '10px';
    style.border = '1px solid #b6e0fe';
    style.boxShadow = '0 12px 30px rgba(15, 60, 92, 0.2)';
    style.fontWeight = '700';
    style.fontSize = '0.95rem';
    style.pointerEvents = 'none';
    style.opacity = '0';
    style.transition = `opacity ${TOAST_ANIMATION_MS}ms ease, transform ${TOAST_ANIMATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
    style.willChange = 'transform, opacity';
    style.zIndex = '9999';
    style.maxWidth = '90%';
    style.textAlign = 'center';
    style.backdropFilter = 'blur(6px)';

    document.body.appendChild(toast);
    toastRef.current = toast;

    // Trigger entrance animation
    window.requestAnimationFrame(() => {
      if (!toastRef.current) return;
      toastRef.current.style.opacity = '1';
      toastRef.current.style.transform = 'translate(-50%, 0) scale(1)';
    });

    // Hide after duration
    const hide = () => {
      if (!toast.isConnected) return;
      toast.style.opacity = '0';
      toast.style.transform = 'translate(-50%, -24px) scale(0.9)';
      safeSetTimeout(() => {
        toast.remove();
        if (toastRef.current === toast) {
          toastRef.current = null;
        }
      }, TOAST_ANIMATION_MS);
    };

    safeSetTimeout(hide, TOAST_DISPLAY_MS);
  }, [safeSetTimeout]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastNotification = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastNotification must be used within a ToastProvider');
  }
  return context;
};
